import * as Sentry from '@sentry/react'
import { Spin } from 'antd'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { Navigate, Route, Routes } from 'react-router-dom'
import Cases from '~/pages/Admin/Cases'
import CaseDetails from '~/pages/Admin/Cases/CaseDetails'
import CouponCodePage from '~/pages/Admin/CouponCodes'
import CourseDetails from '~/pages/Admin/CourseDetails'
import Courses from '~/pages/Admin/Courses'
import { CreateUpdateCourse } from '~/pages/Admin/Courses/CreateUpdateCourse'
import EmailTemplates from '~/pages/Admin/EmailTemplates'
import Licenses from '~/pages/Admin/Licenses'
import Softwares from '~/pages/Admin/Softwares'
import UtilizationReport from '~/pages/Admin/UltilizationReport'
import Users from '~/pages/Admin/Users'
import { CreateUser } from '~/pages/Admin/Users/CreateUser'
import { EditUser } from '~/pages/Admin/Users/EditUser'
import Workstations from '~/pages/Admin/Workstations'
import WorkstationDetails from '~/pages/Admin/Workstations/WorkstationDetails'
import UploadLocations from '~/pages/Admin/UploadLocations'
import ChangePassword from '~/pages/ChangePassword'
import CancelRegistrationForm from '~/pages/CourseRegistrationForm/CancelRegistrationForm'
import DoctorCases from '~/pages/DoctorCases'
import DoctorCourseDetails from '~/pages/DoctorCourseDetails'
import DoctorCourses from '~/pages/DoctorCourses'
import DoctorWorkstationDetails from '~/pages/DoctorWorkstationDetails'
import DoctorWorkstations from '~/pages/DoctorWorkstations'
import RegistrationSuccess from '~/pages/RegistrationSuccess'
import RegistrationSummary from '~/pages/RegistrationSummary'
import ForgotPassword from '~/pages/SignIn/ForgotPassword'
import ResetPassword from '~/pages/SignIn/ResetPassword'
import TermsAndConditions from '~/pages/TermsAndConditions'
import UploadFiles from '~/pages/UploadFiles'
import Workstation from '~/pages/Workstation'
import WorkstationInfo from '~/pages/WorkstationInfo'
import { selectAuthToken } from '~/store/auth/selector'
import CourseRegistrationForm from '../../pages/CourseRegistrationForm'
import SignIn from '../../pages/SignIn/SignIn'
import AdminLayout from './AdminLayout'
import UserLayout from './UserLayout'
import { useProfileQuery } from './query'
import { COURSE_EVENT_SIGNUP, SIGNIN } from './routes'
import './styles.scss'

const SentryRoutes = Sentry.wrapReactRouterRouting(Routes)

const MasterLayout = ({ isAuthenticated = false }) => {
  const authToken = useSelector(selectAuthToken)
  const { isInitialLoading: isLoadingProfile, data: user } = useProfileQuery(authToken)

  if (isLoadingProfile) {
    return (
      <div className='centered-container'>
        <Spin size='large' />
      </div>
    )
  }

  return (
    <SentryRoutes>
      {!isAuthenticated && (
        <>
          <Route
            exact
            path={SIGNIN}
            element={<SignIn isAuthenticated={isAuthenticated} />}
          />
          <Route exact path={'change-password'} element={<ChangePassword />} />
        </>
      )}

      <Route
        exact
        path={COURSE_EVENT_SIGNUP}
        element={<CourseRegistrationForm />}
      />
      <Route exact path='workstations/:token' element={<Workstation />} />
      <Route
        exact
        path='workstation-info/:token'
        element={<WorkstationInfo />}
      />
      <Route
        path='courses/:token/cancel-registration'
        element={<CancelRegistrationForm />}
      />
      <Route exact path='forgot-password' element={<ForgotPassword />} />
      <Route exact path='reset-password' element={<ResetPassword />} />
      <Route
        exact
        path='registrations/:id/summary'
        element={<RegistrationSummary />}
      />
      <Route
        exact
        path='registrations/success'
        element={<RegistrationSuccess />}
      />

      <Route
        exact
        path='terms-and-conditions'
        element={<TermsAndConditions />}
      />

      {isAuthenticated && user.role === 'admin' && (
        <>
          <Route path='admin' element={<AdminLayout />}>
            <Route path='courses' element={<Courses />} />
            <Route exact path='courses/:id' element={<CourseDetails />} />
            <Route
              exact
              path='courses/create'
              element={<CreateUpdateCourse />}
            />
            <Route
              exact
              path='courses/edit/:id'
              element={<CreateUpdateCourse />}
            />
            <Route exact path='users' element={<Users />} />
            <Route exact path='users/create' element={<CreateUser />} />
            <Route exact path='users/edit/:id' element={<EditUser />} />
            <Route exact path='cases' element={<Cases />} />
            <Route exact path='courses/:id/cases' element={<CaseDetails />} />
            <Route
              exact
              path='courses/:id/email-templates'
              element={<EmailTemplates />}
            />
            <Route
              exact
              path='courses/:id/workstations'
              element={<WorkstationDetails />}
            />
            <Route exact path='workstations' element={<Workstations />} />
            <Route
              exact
              path='utilization-report'
              element={<UtilizationReport />}
            />
            <Route exact path='softwares' element={<Softwares />} />
            <Route exact path='licenses' element={<Licenses />} />
            <Route exact path='email-templates' element={<EmailTemplates />} />
            <Route exact path='coupon-codes' element={<CouponCodePage />} />
            <Route exact path='upload-locations' element={<UploadLocations />} />
          </Route>
          <Route path='*' element={<Navigate to='admin/courses' replace />} />
        </>
      )}

      {isAuthenticated && user.role !== 'admin' && (
        <>
          <Route path='user' element={<UserLayout />}>
            <Route exact path='courses' element={<DoctorCourses />} />
            <Route exact path='courses/:id' element={<DoctorCourseDetails />} />
            <Route exact path='cases' element={<DoctorCases />} />
            <Route exact path='courses/:id/cases' element={<UploadFiles />} />
            <Route exact path='workstations' element={<DoctorWorkstations />} />
            <Route
              exact
              path='courses/:id/workstations'
              element={<DoctorWorkstationDetails />}
            />
          </Route>
          <Route path='*' element={<Navigate to='user/courses' replace />} />
        </>
      )}

      <Route path='*' element={<Navigate to={SIGNIN} replace />} />
    </SentryRoutes>
  )
}

MasterLayout.propTypes = {
  isAuthenticated: PropTypes.bool
}

export default MasterLayout
